// Portrait matches — the web half. Same documents and endpoint as iOS:
// preferences/matchingConsent (portraitConsentGrantedAtMs), the one-sided
// projections at users/{uid}/portraitMatchProposals, portraitMatchRespond,
// and the shared match-conversation transport the Matches view already
// renders. Doctrine: its own consent, never inherited; the other person's
// words never appear in any payload; silence is preserved.

import {
  collection,
  deleteField,
  doc,
  getDoc,
  onSnapshot,
  query,
  setDoc,
  Timestamp,
  where,
} from "firebase/firestore";
import { auth, db } from "./firebase";
import { state } from "./state";
import { render as _render } from "./ui";
import { escapeHtml, showToast } from "./utils";
import type { PortraitMatchProposal, PortraitMatchingState } from "./types";

const RESPOND_URL = "https://us-central1-language-of-thoughts.cloudfunctions.net/portraitMatchRespond";
const SUPPORTED_SCHEMA = 1;

export function initialPortraitMatchingState(): PortraitMatchingState {
  return { enabled: false, resolved: false, consent: false, proposals: [], responding: false, error: null };
}

let stopProposals: (() => void) | null = null;

export function stopPortraitMatching(): void {
  stopProposals?.();
  stopProposals = null;
}

export function resetPortraitMatching(): void {
  stopPortraitMatching();
  state.portraitMatching = initialPortraitMatchingState();
}

function parseProposal(raw: Record<string, unknown>): PortraitMatchProposal | null {
  if (Number(raw.schemaVersion) !== SUPPORTED_SCHEMA) return null;
  const pairId = String(raw.pairId || "");
  if (!pairId || raw.provenance !== "because_your_portraits_overlap") return null;
  const claimIds = Array.isArray(raw.selfOverlapClaimIds) ? (raw.selfOverlapClaimIds as string[]).map(String) : null;
  if (!claimIds) return null;
  return {
    pairId,
    selfOverlapClaimIds: claimIds,
    overlapCount: Number(raw.overlapCount || claimIds.length),
    expiresAtMs: Number(raw.expiresAtMs || 0),
    status: String(raw.status || "proposed"),
  };
}

export async function startPortraitMatching(): Promise<void> {
  stopPortraitMatching();
  const uid = state.user?.uid;
  const pm = state.portraitMatching;
  if (!uid) return;
  try {
    const flag = await getDoc(doc(db, "appConfig", "ai_v1"));
    pm.enabled = (flag.exists() ? flag.data() : {})?.portraitMatchingRollout === true;
  } catch {
    pm.enabled = false;
  }
  try {
    const consent = await getDoc(doc(db, "users", uid, "preferences", "matchingConsent"));
    pm.consent = !!(consent.exists() ? consent.data() : {})?.portraitConsentGrantedAtMs;
  } catch {
    pm.consent = false;
  }
  pm.resolved = true;
  _render();
  if (!pm.enabled) return;
  stopProposals = onSnapshot(
    query(collection(db, "users", uid, "portraitMatchProposals"), where("status", "in", ["proposed", "accepted_waiting"])),
    (snapshot) => {
      pm.proposals = snapshot.docs
        .map((d) => parseProposal(d.data() as Record<string, unknown>))
        .filter((p): p is PortraitMatchProposal => !!p)
        .filter((p) => p.expiresAtMs <= 0 || p.expiresAtMs > Date.now());
      _render();
    },
    () => {
      pm.proposals = [];
      _render();
    },
  );
}

/** Its own consent. Local state at once; the document is authoritative and
 * the server trigger mirrors it into the pool, so revocation on this device
 * removes eligibility everywhere. */
export async function setPortraitMatchConsent(granted: boolean): Promise<void> {
  const uid = state.user?.uid;
  const pm = state.portraitMatching;
  if (!uid || !pm.enabled) return;
  const previous = pm.consent;
  pm.consent = granted;
  if (!granted) pm.proposals = [];
  _render();
  try {
    await setDoc(doc(db, "users", uid, "preferences", "matchingConsent"), {
      schemaVersion: 1,
      portraitConsentGrantedAtMs: granted ? Date.now() : deleteField(),
      updatedAt: Timestamp.now(),
    }, { merge: true });
  } catch {
    pm.consent = previous;
    showToast("That didn't save. Check your connection and try again.", "error");
    _render();
  }
}

/** True only when the server confirmed the transition. */
export async function respondPortraitMatch(pairId: string, accept: boolean): Promise<boolean> {
  const pm = state.portraitMatching;
  if (pm.responding) return false;
  pm.responding = true;
  pm.error = null;
  _render();
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("not_signed_in");
    const token = await user.getIdToken();
    const response = await fetch(RESPOND_URL, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ pairId, action: accept ? "accept" : "decline" }),
    });
    const json = await response.json().catch(() => ({})) as Record<string, unknown>;
    if (response.status !== 200 || typeof json.status !== "string" || typeof json.transitioned !== "boolean") throw new Error("respond_failed");
    return json.transitioned === true;
  } catch {
    pm.error = "That didn't get through. You can try again.";
    return false;
  } finally {
    pm.responding = false;
    _render();
  }
}

// ── Rendering (a section inside the Matches view) ───────────────────────────

function statementsFor(claimIds: string[]): string[] {
  const byId = new Map(state.portrait.claims.map((c) => [c.id, c.statement]));
  return claimIds.map((id) => byId.get(id)).filter((s): s is string => !!s).slice(0, 5);
}

export function renderPortraitMatchingSection(): string {
  const pm = state.portraitMatching;
  if (!pm.resolved || !pm.enabled) return "";
  const busy = pm.responding ? "disabled" : "";
  const proposals = pm.proposals.map((proposal) => {
    const waiting = proposal.status === "accepted_waiting";
    const statements = statementsFor(proposal.selfOverlapClaimIds);
    return `
      <article class="portrait-match-card" data-key="pm:${escapeHtml(proposal.pairId)}">
        <div class="portrait-eyebrow"><span>SOMEONE'S PORTRAIT OVERLAPS WITH YOURS</span></div>
        <p class="portrait-match-lede">${proposal.overlapCount > 1 ? `${proposal.overlapCount} parts of your portrait echo theirs.` : "A part of your portrait echoes theirs."}</p>
        ${statements.length ? `<ul class="portrait-match-list">${statements.map((s) => `<li>${escapeHtml(s)}</li>`).join("")}</ul>` : `<p class="portrait-caption">Loading the parts of your portrait this touches…</p>`}
        <p class="portrait-caption">Only your side is ever shown to you. They see only theirs.</p>
        ${waiting ? `
          <div class="portrait-match-waiting">
            <strong>You said you'd talk.</strong>
            <p>If they feel the same, a conversation opens here. They're never told otherwise.</p>
            <button class="btn-ghost btn-sm" data-action="portrait-match-respond" data-pair-id="${escapeHtml(proposal.pairId)}" data-accept="false" ${busy}>Change my mind</button>
          </div>` : `
          <div class="portrait-actions-row">
            <button class="btn-primary" data-action="portrait-match-respond" data-pair-id="${escapeHtml(proposal.pairId)}" data-accept="true" ${busy}>I'd talk</button>
            <button class="btn-secondary" data-action="portrait-match-respond" data-pair-id="${escapeHtml(proposal.pairId)}" data-accept="false" ${busy}>Not now</button>
          </div>`}
      </article>`;
  }).join("");

  const status = pm.consent
    ? (pm.proposals.length ? "" : `
      <div class="portrait-surface portrait-match-state">
        <strong class="portrait-title">Looking for a meaningful overlap</strong>
        <p class="portrait-body">We'll let you know when several parts of someone else's portrait genuinely echo yours. Your words stay private while we look.</p>
      </div>`)
    : `
      <div class="portrait-surface portrait-match-state">
        <strong class="portrait-title">Let your portrait look for someone</strong>
        <p class="portrait-body">LOT compares only the observations you've confirmed — never sensitive ones — and looks for a person whose portrait overlaps with yours in more than one way. Nobody sees your words unless you share them yourself. You can turn this off at any time.</p>
        <button class="btn-primary" data-action="portrait-match-consent" data-granted="true">Let it look</button>
      </div>`;

  return `
    <section class="match-section portrait-matching">
      ${pm.error ? `<div class="settings-note">${escapeHtml(pm.error)}</div>` : ""}
      ${proposals ? `<div class="match-stack">${proposals}</div>` : ""}
      ${status}
      ${pm.consent ? `<button class="portrait-link portrait-match-off" data-action="portrait-match-consent" data-granted="false">Stop looking</button>` : ""}
    </section>
  `;
}

export async function handlePortraitMatchingAction(action: string, target: Element): Promise<boolean> {
  const el = target as HTMLElement;
  switch (action) {
    case "portrait-match-consent":
      await setPortraitMatchConsent(el.dataset["granted"] === "true");
      return true;
    case "portrait-match-respond": {
      const pairId = el.dataset["pairId"] || "";
      if (!pairId) return true;
      await respondPortraitMatch(pairId, el.dataset["accept"] === "true");
      return true;
    }
    default:
      return false;
  }
}
